import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import ShareModal from './ShareModal';

const { FiPlay, FiPause, FiVolume2, FiZap, FiMusic, FiAlertCircle, FiRadio, FiDisc, FiShare2 } = FiIcons;

const CutePlayer = ({ streamUrl, logoUrl, stationName = "Console Radio", shareUrl }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [volume, setVolume] = useState(0.8);
  const [metadata, setMetadata] = useState({ 
    title: "Live Stream", 
    artist: stationName,
    album: "Digital Broadcast"
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isShareOpen, setIsShareOpen] = useState(false);
  const audioRef = useRef(null);

  useEffect(() => {
    const updateMetadata = () => {
      if ('mediaSession' in navigator && navigator.mediaSession.metadata) {
        const { title, artist, album } = navigator.mediaSession.metadata;
        setMetadata({
          title: title || "Live Stream",
          artist: artist || stationName,
          album: album || "On Air"
        });
      }
    };
    const interval = setInterval(updateMetadata, 3000);
    return () => clearInterval(interval);
  }, [stationName]);

  const getSafeStreamUrl = (url) => {
    if (!url) return "";
    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}cb=${Date.now()}`;
  };

  const togglePlay = () => {
    if (!streamUrl) { setError("No stream URL"); return; }
    if (isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
    } else {
      setError(null);
      setIsLoading(true);
      audioRef.current.src = getSafeStreamUrl(streamUrl);
      audioRef.current.load();
      const playPromise = audioRef.current.play();
      if (playPromise !== undefined) {
        playPromise
          .then(() => {
            setIsPlaying(true);
            setIsLoading(false);
            if ('mediaSession' in navigator) {
              navigator.mediaSession.metadata = new MediaMetadata({
                title: 'Live Stream',
                artist: stationName,
                album: 'Console Radio',
                artwork: logoUrl ? [{ src: logoUrl, sizes: '512x512', type: 'image/png' }] : []
              });
            }
          })
          .catch(() => {
            setIsLoading(false);
            setIsPlaying(false);
            setError("Stream Offline");
          });
      }
    }
  };

  useEffect(() => {
    if (audioRef.current) audioRef.current.volume = volume;
  }, [volume]);

  return (
    <div className="w-full bg-white rounded-[3rem] shadow-[0_32px_64px_-16px_rgba(0,0,0,0.1)] border border-gray-100 overflow-hidden flex flex-col md:flex-row min-h-[420px] relative group">
      
      {/* Visual Section */}
      <div className="w-full md:w-[380px] bg-[#0A0A0B] relative overflow-hidden shrink-0 flex items-center justify-center">
        <AnimatePresence mode="wait">
          {logoUrl ? (
            <motion.img 
              key="logo"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              src={logoUrl} 
              className={`w-full h-full object-cover absolute inset-0 transition-transform duration-[15000ms] ${isPlaying ? 'scale-125' : 'scale-100'}`} 
              alt="Cover"
            />
          ) : (
            <motion.div key="vinyl" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="relative w-64 h-64 flex items-center justify-center">
              <motion.div 
                animate={isPlaying ? { rotate: 360 } : { rotate: 0 }}
                transition={{ repeat: Infinity, duration: 4, ease: "linear" }}
                className="w-full h-full rounded-full bg-gradient-to-tr from-[#111] via-[#222] to-[#111] shadow-2xl flex items-center justify-center p-1 border-4 border-white/5"
              >
                <div className="w-full h-full rounded-full border border-white/10 flex items-center justify-center">
                  <div className="w-20 h-20 rounded-full bg-[#00AEEF] flex items-center justify-center shadow-inner">
                    <SafeIcon icon={FiDisc} className="text-white/40 text-4xl" />
                  </div>
                </div>
              </motion.div>
              <motion.div 
                animate={isPlaying ? { rotate: 15 } : { rotate: -15 }}
                className="absolute top-0 right-0 w-1 h-32 bg-gray-400 origin-top transform transition-transform duration-700"
                style={{ clipPath: 'polygon(0 0, 100% 0, 70% 100%, 30% 100%)' }}
              />
            </motion.div>
          )}
        </AnimatePresence>
        
        <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent opacity-60"></div>
        <div className="absolute bottom-6 left-6 flex items-center gap-3">
          <div className="w-2 h-2 rounded-full bg-[#00AEEF] animate-pulse" />
          <span className="text-[10px] font-black text-white uppercase tracking-[0.3em]">{stationName}</span>
        </div>
      </div>

      {/* Control Section */}
      <div className="flex-1 p-10 md:p-16 bg-white flex flex-col justify-center relative">
        {/* Share Button Trigger */}
        {shareUrl && (
          <button 
            onClick={() => setIsShareOpen(true)}
            className="absolute top-10 right-10 p-4 bg-gray-50 text-gray-400 hover:bg-[#00AEEF] hover:text-white rounded-2xl transition-all shadow-sm flex items-center gap-2 group/btn"
          >
            <SafeIcon icon={FiShare2} className="text-lg" />
            <span className="text-[10px] font-black uppercase tracking-widest hidden group-hover/btn:block">Share Player</span>
          </button>
        )}

        {error && (
          <div className="absolute top-6 left-10 right-10">
            <div className="bg-red-50 text-red-500 text-[10px] font-black uppercase tracking-widest p-3 rounded-xl border border-red-100 flex items-center gap-2">
              <SafeIcon icon={FiAlertCircle} /> {error}
            </div>
          </div>
        )}

        <div className="mb-12 space-y-2">
          <p className="text-[10px] font-black text-[#00AEEF] uppercase tracking-[0.4em]">Now Playing</p>
          <motion.h2 
            key={metadata.title}
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            className="text-4xl md:text-6xl font-black text-gray-900 tracking-tighter leading-none"
          >
            {metadata.title}
          </motion.h2>
          <div className="flex items-center gap-3 pt-2">
            <span className="text-sm font-bold text-gray-400 uppercase tracking-widest">{metadata.artist}</span>
            <div className="w-1 h-1 rounded-full bg-gray-200" />
            <span className="text-sm font-bold text-gray-300 uppercase tracking-widest">{metadata.album}</span>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row items-center gap-10">
          <button 
            onClick={togglePlay}
            disabled={isLoading}
            className="w-24 h-24 rounded-[2.5rem] bg-gray-900 text-white flex items-center justify-center shadow-2xl hover:bg-[#00AEEF] transition-all hover:scale-105 active:scale-95 disabled:opacity-50 group"
          >
            {isLoading ? (
              <div className="w-8 h-8 border-4 border-white/20 border-t-white rounded-full animate-spin"></div>
            ) : isPlaying ? (
              <SafeIcon icon={FiPause} className="text-4xl" />
            ) : (
              <SafeIcon icon={FiPlay} className="text-4xl ml-1 group-hover:text-white" />
            )}
          </button>

          <div className="flex-1 w-full space-y-4">
            <div className="flex justify-between text-[10px] font-black text-gray-400 uppercase tracking-widest">
              <span className="flex items-center gap-2"><SafeIcon icon={FiVolume2} /> Master Volume</span>
              <span>{Math.round(volume * 100)}%</span>
            </div>
            <input 
              type="range" 
              min="0" 
              max="1" 
              step="0.01" 
              value={volume} 
              onChange={(e) => setVolume(parseFloat(e.target.value))}
              className="w-full h-1.5 bg-gray-100 rounded-lg appearance-none cursor-pointer accent-[#00AEEF]"
            />
          </div>
        </div>
      </div>

      <audio ref={audioRef} preload="none" />
      
      <ShareModal 
        isOpen={isShareOpen} 
        onClose={() => setIsShareOpen(false)} 
        shareUrl={shareUrl} 
      />
    </div>
  );
};

export default CutePlayer;